import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

/** Generic list page for platform / care simple GET endpoints. */
export default function GenericListPage({
  eyebrow,
  title,
  subtitle,
  url,
  extract = (d) => (Array.isArray(d) ? d : (d?.items || d?.list || Object.values(d || {}).find(Array.isArray) || [])),
  columns = "md:grid-cols-2 lg:grid-cols-3",
  emptyIcon,
  emptyTitle,
  renderItem,
  searchFields = ["name", "email", "title"],
  actions,
  testId,
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const q = useQuery({
    queryKey: [testId, url],
    queryFn: async () => (await api.get(url)).data,
    retry: false,
  });

  const items = useMemo(() => {
    const arr = extract(q.data) || [];
    if (!query) return arr;
    const s = query.toLowerCase();
    return arr.filter((it) =>
      searchFields.some((f) => String(it?.[f] || "").toLowerCase().includes(s))
    );
  }, [q.data, query, extract, searchFields]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} actions={actions} />
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-ink-500" strokeWidth={1.75} />
        <Input
          data-testid={`${testId}-search`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("common.search")}
          className="rounded-xl bg-white h-11 max-w-md"
        />
      </div>
      {q.isLoading && <LoadingCard />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={emptyIcon} title={emptyTitle || t("common.empty")} testId={`${testId}-empty`} />
      )}
      <div className={`grid grid-cols-1 ${columns} gap-3`} data-testid={`${testId}-list`}>
        {items.map((it, i) => renderItem(it, i))}
      </div>
    </div>
  );
}
